import React, { useCallback, useMemo, useState } from "react";
import { Alert, InputNumber, Switch, Select, Divider, Button } from "antd";
import { faPlugCircleCheck, faCodeBranch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
// INTERFACES
import { FormularyType } from "../../../interfaces/Wizard.interface";
// COMPONENTS
import decodeStoredMceMapping from "../../../flatbuffers/decoders/mceMapping";
import GETJwtToken from "../../../redux/authentication/authentication";
import useBankEventAddressStatus from "../../../customHooks/GET_requests/useBankEventAddressStatus";
import { isLegacyEventEditable } from "../../../utils/mceIdl/eventMappingEdit";
import { READ_BACK_FAILED, readStoredMapping } from "../../../utils/mceIdl/saveUpload";
import { diagnosticRows } from "../../../utils/mceIdl/tableView";
import MceIdlUpload, { MceUploadOutcome } from "./mce/MceIdlUpload";
import MceLegacyEventFields, { MceEventField } from "./mce/MceLegacyEventFields";
import MceMappingReview from "./mce/MceMappingReview";
import DrawerRowDivider from "../../components/drawer/DrawerRowDivider";
import FormInputText from "../../components/form/FormInputText";
// CDO EDITORS
import CdoOpcPage from "../../edgeManagement/adapters/CDO/OPC/CdoOpcPage";
import CdoOpcUaPage from "../../edgeManagement/adapters/CDO/OPCUA/CdoOpcUaPage";
import CdoLobbyVision2Page from "../../edgeManagement/adapters/CDO/LOBBYVISION2/CdoLobbyVision2Page";
import CdoSequentPage from "../../edgeManagement/adapters/CDO/SEQUENT/CdoSequentPage";
import CdoModbusEditor from "./CdoModbusEditor";
import CdoStm32Editor from "./CdoStm32Editor";
import CdoNumatoRelayEditor from "./CdoNumatoRelayEditor";
import CdoNumatoGpioEditor from "./CdoNumatoGpioEditor";
import CdoLn1MappingEditor, { Ln1KeyMappingRow } from "./CdoLn1MappingEditor";
import CdoBacnetPage from "../../edgeManagement/adapters/CDO/BACNET/CdoBacnetPage";
import OneHotExclusionEditor from "./OneHotExclusionEditor";
// BINARY CDO PROTOCOL REGISTRY
import {
  OPC_UA_BINARY_CDO_CONFIG,
  LOBBYVISION2_BINARY_CDO_CONFIG,
} from "../editors/BinaryCdoProtocolRegistry";
// INTERFACES
import {
  AdapterCdoMceInterface,
  AdapterCdoLn1Interface,
  AdapterCdoSequentInterface,
  AdapterBacnetInterface,
  BacnetBankMapping,
} from "../../../interfaces/Adapter.interface";
import { HeartbeatKind } from "../../../types/enums";
import type { EditorModbusTemplate } from "./CdoModbusEditor";
import { AdapterCdoOpcInterface } from "../../../interfaces/AdapterCdoOpc.interface";
import { AdapterCdoOpcUa } from "../../../interfaces/AdapterCdoOpcUa.interface";
import { AdapterCdoLobbyVision2 } from "../../../interfaces/AdapterCdoLobbyVision2.interface";
// FLATBUFFER TYPES
import { AdapterProtocol } from "../../../flatbuffers/generated/archive/adapter-protocol";
import { MessageContent } from "../../../flatbuffers/generated/ln2/message-content";
import { OneHotExclusionConfigT } from "../../../flatbuffers/generated/one-hot-exclusion/one-hot-exclusion-config";
import { ValuedMessageT } from "../../../flatbuffers/generated/ln2/valued-message";
import {
  Ln2MessageFilter,
  parseCompositeKey,
  categoryKeyToMessageContent,
  messageContentToCategoryKey,
} from "../../../flatbuffers/types/ln2MessageOptions";
import { valuedMessageTToCompositeKey } from "../../../flatbuffers/types/valuedMessageBuilder";
import {
  numatoCdoMessagesAreConfigured,
  stm32CdoMessagesAreConfigured,
} from "../../../flatbuffers/types/numatoFlatMessage";
import { numatoRelayControllerProblems } from "./numatoRelayController";
import { numatoU128Problems } from "./numatoU128Fields";
import { ProductType } from "../../../flatbuffers/generated/mce/product-type";
import { logError } from "../../../utils/devLog";
// STORE
import { RootState } from "../../../app/store";
// OVERLAP DETECTION
import {
  detectMappingRowOverlaps,
  type MappingRowForDetection,
} from "../../components/adapters/offsetOverlapDetection";

// ************************************************ */
// MCE PRODUCT TYPE ENUM — auto-derived from FlatBuffer ProductType */
const MCE_PRODUCT_TYPES = Object.entries(ProductType)
  .filter(([, v]) => typeof v === "number")
  .map(([label, value]) => ({ value: value as number, label }));

// ************************************************ */
// PROTOCOL NAMES — auto-derived from FlatBuffer AdapterProtocol */
/**
 * The protocol string these branches compare against is produced by
 * `AdapterProtocol[adapterData.protocolType]` in AdapterEditor.tsx, so it is
 * always the generated enum's own member name. Spelling it by hand is how
 * `NUMATO_GPIO` came to be compared against a value that is really
 * `NUMATOGPIO`: the branch simply never ran, and the mapping editor never
 * mounted. Deriving the strings from the enum makes that class of typo
 * impossible and keeps the branches correct if a member is ever renamed.
 */
export const NUMATO_GPIO = AdapterProtocol[AdapterProtocol.NUMATOGPIO];
export const NUMATO_RELAY = AdapterProtocol[AdapterProtocol.NUMATORELAY];

// ************************************************ */
// CDO STATE TYPE ********************************* */
export interface CdoStateBundle {
  cdoMce: AdapterCdoMceInterface;
  cdoLn1: AdapterCdoLn1Interface;
  cdoModbus: EditorModbusTemplate;
  cdoOpc: AdapterCdoOpcInterface;
  cdoOpcUa: AdapterCdoOpcUa;
  cdoLobbyVision2: AdapterCdoLobbyVision2;
  cdoSequent: AdapterCdoSequentInterface;
  cdoBacnet: AdapterBacnetInterface[];
  bacnetBankMappings: BacnetBankMapping[];
  cdoJsonFallback: string;
  ln1KeyMappings: Ln1KeyMappingRow[];
  ln1UnitOffsets: Partial<Record<MessageContent, number[]>>;
  ln1IntraContentFlags: Partial<Record<MessageContent, boolean[]>>;
  /**
   * Per-individual-message offsets for bank_level key mappings (composite
   * keys like "service:normal:5"). Distinct from ln1UnitOffsets, which is
   * keyed by MessageContent category and used for grouped columns.
   * Stored as [initial, initial + delta] so the adapter matrix can derive
   * Initial/Delta via the same helper used for grouped categories.
   */
  ln1IndividualOffsets?: Partial<Record<string, [number, number]>>;
  oneHotExclusion?: OneHotExclusionConfigT;
  modbusRegisterOffsets?: {
    initial: Partial<Record<"COIL" | "DI" | "HR" | "IR", number>>;
    delta: Partial<Record<"COIL" | "DI" | "HR" | "IR", number>>;
  };
}

export interface BankContextInfo {
  bankId: string;
  bankName: string;
  lifts: { id: string; name: string }[];
}

interface StepAdaptersCdoEditorProps {
  protocolType: string;
  cdo: CdoStateBundle;
  setCdo: React.Dispatch<React.SetStateAction<CdoStateBundle>>;
  units?: number;
  floors?: number;
  onUnitsChange?: (u: number) => void;
  bankContext?: BankContextInfo;
  /** The saved adapter this form edits, when there is one. */
  adapterId?: string;
  defaultDevType?: number;
  /** The owning adapter's direction_aware_keys, forwarded to the LN1 editor. */
  directionAware?: boolean;
}

// ************************************************ */
// VALIDATION ************************************* */

/**
 * Extract the category prefix from a composite LN2 message key.
 * The key format is "categoryKey:variant:enumValue" — the first colon-delimited
 * segment is the category (e.g. "status", "fault"). Returns "" for blank keys.
 */
function mappingCategoryKey(m: Ln1KeyMappingRow): string {
  if (!m.ln2MessageKey) return "";
  return m.ln2MessageKey.split(":")[0] ?? "";
}

/**
 * Which MessageContent categories share a physical register block.
 *
 * `true`/`false` keep the legacy all-or-nothing behaviour. A Set of category
 * keys is the precise form: only the listed categories are treated as sharing
 * a block, so declaring (say) Status+Usage shared does not also force Fault,
 * Control and Group Supervisory — which live at completely different offsets —
 * into one global namespace.
 */
export type IntraContentScope = boolean | ReadonlySet<string>;

/**
 * Convert the persisted `{ [messageContent]: boolean[] }` intra-content flag
 * map into the set of category keys it declares. A category counts as declared
 * when any unit's flag is set.
 */
export function intraContentCategories(
  flags: Record<string | number, unknown> | undefined,
): ReadonlySet<string> {
  const out = new Set<string>();
  if (!flags) return out;
  Object.entries(flags).forEach(([key, value]) => {
    const mc = Number(key);
    if (!Number.isFinite(mc) || mc <= 0) return;
    if (!Array.isArray(value) || !value.some(Boolean)) return;
    const categoryKey = messageContentToCategoryKey(mc);
    if (categoryKey) out.add(categoryKey);
  });
  return out;
}

/**
 * Build the dedup key for a mapping row.
 *
 * @param intraContent - which categories share a register block. `true` means
 *   all of them, so byte+bit must be unique ACROSS every MessageContent.
 *   `false` (default) means none, so byte+bit only needs to be unique WITHIN
 *   the same category — Traffic byte 0 bit 0 and Status byte 0 bit 0 are
 *   allowed because their bank-level offsets separate them at runtime. A Set
 *   of category keys scopes it: the listed categories share one namespace with
 *   each other, every other category keeps its own.
 */
function mappingDedupKey(m: Ln1KeyMappingRow, intraContent: IntraContentScope): string {
  const cat = mappingCategoryKey(m);
  // A category is "shared" when it has been declared intra-content: it reads
  // from the same physical register block as the other declared categories,
  // so their byte+bit positions must not collide with each other.
  const shared = typeof intraContent === "boolean" ? intraContent : intraContent.has(cat);
  if (shared) {
    // Shared: omit the category so collisions BETWEEN declared categories are
    // detected. Categories that were not declared keep their own namespace
    // below, because they live in a different register block entirely.
    return m.contentType === "bit"
      ? `bit:${m.byte_offset}:${m.bit_offset}:${m.data}`
      : `byte:${m.offset}:${m.value}`;
  }
  // Not shared: only enforce uniqueness WITHIN the same category. Traffic@0:0
  // and Status@0:0 are allowed because their offsets separate them at runtime.
  return m.contentType === "bit"
    ? `${cat}:bit:${m.byte_offset}:${m.bit_offset}:${m.data}`
    : `${cat}:byte:${m.offset}:${m.value}`;
}

export function hasLn1DuplicateMappings(
  mappings: Ln1KeyMappingRow[],
  intraContent: IntraContentScope = false,
): boolean {
  // bank_level mappings are resolved individually at the bank matrix —
  // individual mappings are resolved via their own Offset Matrix column —
  // exclude both from adapter-level duplicate detection.
  const grouped = mappings.filter((m) => !m.bank_level && !m.individual);
  const seen = new Set<string>();
  for (let i = 0; i < grouped.length; i += 1) {
    const key = mappingDedupKey(grouped[i], intraContent);
    if (seen.has(key)) return true;
    seen.add(key);
  }
  return false;
}

/**
 * Optional matrix context for `findLn1DuplicateIndices`. When provided, the
 * function delegates to `detectMappingRowOverlaps` which respects adapter-matrix
 * and bank-matrix differentiation — bank_level and individual rows stay flagged
 * until the respective matrix resolves the collision.
 */
export interface FindLn1DuplicateContext {
  adapterMatrix?: Record<string, { initial: number; delta: number }>;
  bankMatrixPerLift?: ReadonlyArray<Record<string, number>>;
  numCars?: number;
  /** The owning adapter's direction_aware_keys. When unset, rows sharing an
   *  address are duplicates regardless of direction, exactly as before. */
  directionAware?: boolean;
}

/** Adapter from Ln1KeyMappingRow to the shared MappingRowForDetection shape. */
function toMappingRowForDetection(row: Ln1KeyMappingRow): MappingRowForDetection {
  return {
    contentType: row.contentType,
    byte_offset: row.byte_offset,
    bit_offset: row.bit_offset,
    data: row.data,
    offset: row.offset,
    value: row.value,
    bank_level: row.bank_level,
    individual: row.individual,
    ln2MessageKey: row.ln2MessageKey,
    is_pair_secondary: row.is_pair_secondary,
    floors_based: row.floors_based,
    direction: row.direction,
  };
}

/**
 * Returns a Set of row indices that are duplicates of another row.
 * ALL rows sharing a duplicate key are marked (not just the second one).
 *
 * @param intraContent - when false (default), only same-category same-position
 *   rows are flagged. When true, cross-category same-position rows are also flagged.
 * @param context - optional matrix context. When absent, preserves the legacy
 *   behavior (bank_level rows excluded, no matrix-differentiation check). When
 *   provided, delegates to detectMappingRowOverlaps so bank_level / individual
 *   rows remain flagged until the matrix differentiates the collision.
 */
export function findLn1DuplicateIndices(
  mappings: Ln1KeyMappingRow[],
  intraContent: IntraContentScope = false,
  context?: FindLn1DuplicateContext,
): Set<number> {
  // Preserve legacy behavior when context absent (all existing call sites
  // that pass only (mappings, intraContent) continue to work unchanged).
  if (!context || !context.adapterMatrix) {
    const keyToIndices = new Map<string, number[]>();
    for (let i = 0; i < mappings.length; i += 1) {
      // bank_level and individual mappings each resolve via their own matrix
      // column — exclude both from adapter-level duplicate detection.
      if (!mappings[i].bank_level && !mappings[i].individual) {
        const key = mappingDedupKey(mappings[i], intraContent);
        const arr = keyToIndices.get(key) || [];
        arr.push(i);
        keyToIndices.set(key, arr);
      }
    }
    const dupes = new Set<number>();
    keyToIndices.forEach((indices) => {
      if (indices.length > 1) {
        indices.forEach((idx) => dupes.add(idx));
      }
    });
    return dupes;
  }

  // Context-aware path: delegate to the shared module which understands
  // bank_level and individual differentiation semantics.
  return detectMappingRowOverlaps({
    mappings: mappings.map(toMappingRowForDetection),
    adapterMatrix: context.adapterMatrix,
    bankMatrixPerLift: context.bankMatrixPerLift ?? [],
    numCars: context.numCars ?? 2,
    // Dedup keys already encode intra-content — pass () => false so the
    // detector doesn't double-apply intra semantics.
    intra: () => false,
    directionAware: context.directionAware,
  });
}

/**
 * Validate exclusion config if present. Returns true when absent or valid.
 * Invalid when: duplicate categories, or a non-category-wide group has
 * no entries or an entry has no members.
 */
function isExclusionValid(exclusion: OneHotExclusionConfigT | undefined): boolean {
  if (!exclusion) return true;
  const categories = exclusion.groups.map((g) => g.category);
  if (new Set(categories).size !== categories.length) return false;
  return exclusion.groups.every(
    (g) => g.categoryWide || (g.entries.length > 0 && g.entries.every((e) => e.members.length > 0)),
  );
}

/**
 * Whether the adapter form may save this CDO. Editing an MCE row touches its
 * CDO only for a legacy event mapping edit, which is checked when it is saved,
 * so a rename is never blocked. A new MCE adapter needs an uploaded mapping,
 * which today only "Duplicate" of an embedded adapter can supply.
 */
export function isCdoSaveValid(
  protocolType: string,
  cdo: CdoStateBundle,
  formAction: "create" | "edit" | "duplicate",
): boolean {
  if (protocolType.toUpperCase() !== "MCE") return isCdoValid(protocolType, cdo);
  if (!isExclusionValid(cdo.oneHotExclusion)) return false;
  return formAction === "edit" || cdo.cdoMce.stored?.kind === "embedded";
}

export function isCdoValid(protocolType: string, cdo: CdoStateBundle): boolean {
  if (!isExclusionValid(cdo.oneHotExclusion)) return false;

  const proto = protocolType.toUpperCase();
  switch (proto) {
    case "MCE": {
      // Validity comes from what is stored; there is nothing to type.
      return cdo.cdoMce.stored != null;
    }
    case "LN1":
    case "RVU":
    case "CGA":
    case "LOBBYVISION": {
      // At adapter level: reject duplicate bit or byte mappings.
      // The intra-content flag determines whether cross-category byte
      // sharing is allowed — pass it so the validation matches the UI.
      if (cdo.ln1KeyMappings.length > 0) {
        // Per-category, not a flattened boolean: declaring one pair of
        // categories shared must not impose global cross-category uniqueness
        // on categories that sit in unrelated register blocks.
        const sharedCategories = intraContentCategories(cdo.ln1IntraContentFlags);
        return !hasLn1DuplicateMappings(cdo.ln1KeyMappings, sharedCategories);
      }
      return true;
    }
    case "LN2":
    case "DILIGENT":
      return true;
    case "MODBUS":
      return (cdo.cdoModbus?.reads?.length ?? 0) > 0 || (cdo.cdoModbus?.writes?.length ?? 0) > 0;
    case "OPC":
      return cdo.cdoOpc.opc_server.trim() !== "";
    case "OPCUA":
      return OPC_UA_BINARY_CDO_CONFIG.isValid(cdo.cdoOpcUa);
    case "LOBBYVISION2":
      return LOBBYVISION2_BINARY_CDO_CONFIG.isValid(cdo.cdoLobbyVision2);
    case "SEQUENT":
      return cdo.cdoSequent.discrete_2_message.length > 0;
    case "BACNET":
      return cdo.cdoBacnet.length > 0;
    case "STM32": {
      try {
        const parsed = JSON.parse(cdo.cdoJsonFallback);
        // Every message mapping must name a real LN2 message: message_content 0
        // is rejected with a 400 at PUT, so an unconfigured row blocks save.
        return (
          !!parsed.board_name && !!parsed.board_address && stm32CdoMessagesAreConfigured(parsed)
        );
      } catch (err) {
        logError("[StepAdaptersCdoEditor] parse STM32 cdoJsonFallback failed:", err);
        return false;
      }
    }
    case NUMATO_RELAY: {
      try {
        const parsed = JSON.parse(cdo.cdoJsonFallback);
        const hasEntries =
          (Array.isArray(parsed.response_to_message) && parsed.response_to_message.length > 0) ||
          (Array.isArray(parsed.message_to_cmd) && parsed.message_to_cmd.length > 0);
        // Every row must name a real LN2 message: message_content 0 has no builder
        // in LiftCloud's expander, so an unconfigured row is a 400 at PUT. An
        // optional controller board must also be saveable on its own terms —
        // the server's structure checks and VTS's limits reject it otherwise.
        // Every U128 half (a wide Write All mask) must also be a value the
        // server accepts, or it would silently save as all relays off.
        return (
          hasEntries &&
          numatoCdoMessagesAreConfigured(parsed) &&
          numatoRelayControllerProblems(parsed).length === 0 &&
          numatoU128Problems(parsed, "relay").length === 0
        );
      } catch (err) {
        logError("[StepAdaptersCdoEditor] parse NUMATO cdoJsonFallback failed:", err);
        return false;
      }
    }
    case NUMATO_GPIO: {
      try {
        const parsed = JSON.parse(cdo.cdoJsonFallback);
        const hasEntries =
          (Array.isArray(parsed.response_to_message) && parsed.response_to_message.length > 0) ||
          (Array.isArray(parsed.message_to_cmd) && parsed.message_to_cmd.length > 0);
        // Every row must name a real LN2 message: message_content 0 has no builder
        // in LiftCloud's expander, so an unconfigured row is a 400 at PUT. Every
        // U128 half (Write All / IO Direction / IO Mask / Power On) must also be
        // a value the server accepts, or it would silently save as all zeros.
        return (
          hasEntries &&
          numatoCdoMessagesAreConfigured(parsed) &&
          numatoU128Problems(parsed, "gpio").length === 0
        );
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("[StepAdaptersCdoEditor] parse NUMATO cdoJsonFallback failed:", err);
        return false;
      }
    }
    default:
      return true;
  }
}

/** Human-readable message for what's missing in CDO */
export function getCdoMissingMessage(protocolType: string, cdo?: CdoStateBundle): string {
  if (cdo && !isExclusionValid(cdo.oneHotExclusion)) {
    return "Exclusion groups must have unique categories and non-empty member entries";
  }
  const proto = protocolType.toUpperCase();
  switch (proto) {
    case "MCE":
      return "New MCE adapters are created by uploading an IDL. Choose the product and upload files that build with no errors.";
    case "LN1":
    case "RVU":
    case "CGA":
    case "LOBBYVISION":
      return "Remove duplicate bit (byte_offset, bit_offset, data) or byte (offset, value) mappings";
    case "MODBUS":
      return "Add at least one Modbus mapping entry";
    case "OPC":
      return "OPC requires an OPC server name";
    case "OPCUA":
      return OPC_UA_BINARY_CDO_CONFIG.missingMessage;
    case "LOBBYVISION2":
      return LOBBYVISION2_BINARY_CDO_CONFIG.missingMessage;
    case "SEQUENT":
      return "Add at least one discrete-to-message mapping";
    case "BACNET":
      return "Add at least one BACnet configuration entry";
    case "STM32":
      return "STM32 requires a board name, a board address, and an LN2 message on every message mapping";
    case NUMATO_RELAY: {
      if (cdo?.cdoJsonFallback) {
        try {
          const parsed = JSON.parse(cdo.cdoJsonFallback);
          const controllerProblemsList = numatoRelayControllerProblems(parsed);
          if (controllerProblemsList.length > 0) {
            return `Controller board: ${controllerProblemsList[0]}`;
          }
          const u128ProblemsList = numatoU128Problems(parsed, "relay");
          if (u128ProblemsList.length > 0) {
            return u128ProblemsList[0];
          }
        } catch {
          // Falls through to the generic relay message below — an
          // unparseable fallback is reported elsewhere (isCdoValid's catch).
        }
      }
      return "Add at least one relay mapping entry, and select an LN2 message on every entry";
    }
    case NUMATO_GPIO: {
      if (cdo?.cdoJsonFallback) {
        try {
          const parsed = JSON.parse(cdo.cdoJsonFallback);
          const u128ProblemsList = numatoU128Problems(parsed, "gpio");
          if (u128ProblemsList.length > 0) {
            return u128ProblemsList[0];
          }
        } catch {
          // Falls through to the generic GPIO message below — an
          // unparseable fallback is reported elsewhere (isCdoValid's catch).
        }
      }
      return "Add at least one GPIO mapping entry, and select an LN2 message on every entry";
    }
    default:
      return "Configure the protocol mappings";
  }
}

/**
 * Serialize an exclusion config for the API payload.
 *
 * Members are stored as composite key strings ("usage:normal:4") so the
 * LiftCloud Go packer can reconstruct proper Ln2.MessageT objects without
 * needing to understand the TypeScript Object API JSON format (which uses
 * camelCase union fields incompatible with Go's PascalCase union encoding).
 *
 * Fields follow Go json tag conventions (snake_case). The three group-level
 * flags (`preserve_positive`, `evict_only_when_active`, `emit_deactivations`)
 * must be emitted explicitly — the Go parser reads them off the JSON shape,
 * so dropping them here silently strips user-set values from the binary.
 */
function serializeExclusionConfig(config: OneHotExclusionConfigT): object {
  return {
    groups: config.groups.map((g) => ({
      category: g.category,
      category_wide: g.categoryWide,
      preserve_positive: g.preservePositive,
      evict_only_when_active: g.evictOnlyWhenActive,
      emit_deactivations: g.emitDeactivations,
      entries: g.entries.map((e) => ({
        members: e.members
          .map((m) => {
            const vm = new ValuedMessageT(m, true);
            return valuedMessageTToCompositeKey(vm);
          })
          .filter((k): k is string => k !== null),
      })),
    })),
  };
}

/**
 * Whether this adapter's one-hot exclusion may be edited. An MCE adapter's can
 * only be edited once it is embedded; legacy rows are read-only.
 */
export function isExclusionEditable(protocolType: string, cdo: CdoStateBundle): boolean {
  return protocolType.toUpperCase() !== "MCE" || cdo.cdoMce.stored?.kind === "embedded";
}

/** Build the cdo_input value to send to the API */
export function buildCdoPayload(
  protocolType: string,
  cdo: CdoStateBundle,
): Record<string, unknown> {
  const proto = protocolType.toUpperCase();

  let base: Record<string, unknown>;
  switch (proto) {
    case "MCE":
      // No MCE save carries cdo_input, not even for its exclusion, which has
      // its own field. See AdapterEditor.buildAdapterBody.
      base = {};
      break;
    case "LN1":
    case "RVU":
    case "CGA":
    case "LOBBYVISION":
      base = { ...cdo.cdoLn1 };
      break;
    case "MODBUS":
      // Modbus CDO is sent as FlatBuffer binary via a separate endpoint.
      // Return empty object so cdo_input is not populated with stale JSON.
      base = {};
      break;
    case "OPC":
      base = { ...cdo.cdoOpc };
      break;
    case "OPCUA":
    case "LOBBYVISION2":
      // OPC UA / LobbyVision2 CDO is sent as FlatBuffer binary via a separate
      // endpoint/create-body field (see BinaryCdoProtocolRegistry) — mirrors
      // the MODBUS case. Return empty object so cdo_input is not populated
      // with stale/incompatible JSON.
      base = {};
      break;
    case "SEQUENT":
      base = { ...cdo.cdoSequent };
      break;
    case "BACNET":
      base = {};
      break;
    case "LN2":
    case "DILIGENT":
      base = {};
      break;
    default: {
      try {
        base = JSON.parse(cdo.cdoJsonFallback);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("[StepAdaptersCdoEditor] parse cdoJsonFallback for CDO payload failed:", err);
        base = {};
      }
      break;
    }
  }

  if (proto !== "MCE" && cdo.oneHotExclusion != null) {
    base.one_hot_exclusion = serializeExclusionConfig(cdo.oneHotExclusion);
  }

  return base;
}

/**
 * Extract an Ln2MessageFilter from the adapter's CDO mappings.
 * Collects all LN2 message types referenced in Modbus reads/writes
 * and LN1 key mappings, keyed by their FlatBuffer MessageContent enum.
 */
function extractCdoMessageFilter(cdo: CdoStateBundle): Ln2MessageFilter {
  const filter: Ln2MessageFilter = new Map();

  const addFromKey = (compositeKey: string) => {
    const parsed = parseCompositeKey(compositeKey);
    if (!parsed) return;
    const mc = categoryKeyToMessageContent(parsed.categoryKey);
    if (mc === MessageContent.NONE) return;
    const existing = filter.get(mc);
    if (existing) {
      existing.add(parsed.enumValue);
    } else {
      filter.set(mc, new Set([parsed.enumValue]));
    }
  };

  // Modbus CDO
  cdo.cdoModbus?.reads?.forEach((r) => addFromKey(r.msgId));
  cdo.cdoModbus?.writes?.forEach((w) => addFromKey(w.msgId));

  // LN1 key mappings
  cdo.ln1KeyMappings?.forEach((m) => {
    if (m.ln2MessageKey) addFromKey(m.ln2MessageKey);
  });

  return filter;
}

// ************************************************ */
// COMPONENT ************************************** */
/** The MCE fields that stay read-only are disabled, so this is never called. */
const ignoreMceEdit = (): void => undefined;

type Translate = (key: string) => string;

/** The note above an MCE adapter's mapping: who can change what. */
function mceNoteTitle(mce: AdapterCdoMceInterface, t: Translate): string {
  if (mce.stored?.kind !== "legacy") return "This MCE adapter was built from an uploaded IDL";
  return isLegacyEventEditable(mce)
    ? t("general.mceLegacyEditableTitle")
    : t("general.mceLegacyUnreadableTitle");
}

function mceNoteText(mce: AdapterCdoMceInterface, t: Translate): string {
  if (mce.stored?.kind !== "legacy") return "Its mapping is shown read-only here.";
  return isLegacyEventEditable(mce)
    ? t("general.mceLegacyEditableText")
    : t("general.mceLegacyUnreadableText");
}

export default function StepAdaptersCdoEditor({
  protocolType,
  cdo,
  setCdo,
  units = 1,
  floors = 1,
  onUnitsChange = undefined,
  bankContext = undefined,
  adapterId = undefined,
  defaultDevType = undefined,
  directionAware = false,
}: StepAdaptersCdoEditorProps) {
  const [t] = useTranslation("global");
  const { theme } = useSelector((state: RootState) => state.home);
  const navigate = useNavigate();
  const proto = protocolType.toUpperCase();

  // Message filter: only show LN2 messages that the adapter's CDO actually maps
  const cdoMessageFilter = useMemo(() => extractCdoMessageFilter(cdo), [cdo]);

  // MCE maps messages internally in VTS — show all messages unfiltered.
  // Other protocols restrict to mapped messages (empty filter = undefined = all).
  const exclusionMessageFilter = useMemo(
    () => (proto === "MCE" || cdoMessageFilter.size === 0 ? undefined : cdoMessageFilter),
    [proto, cdoMessageFilter],
  );

  // ************************************************ */

  // A legacy adapter's event mapping is the one part of its mapping edited here.
  const mceEventEditable = proto === "MCE" && isLegacyEventEditable(cdo.cdoMce);
  const eventAddressStatus = useBankEventAddressStatus(adapterId, mceEventEditable);
  const onChangeMceEvent = (field: MceEventField, value: string) => {
    setCdo((prev) => ({
      ...prev,
      cdoMce: { ...prev.cdoMce, event: { ...prev.cdoMce.event, [field]: value } },
    }));
  };

  // An upload replaces what the form shows; a later failed upload puts back what was there.
  const [mceUpload, setMceUpload] = useState<MceUploadOutcome | null>(null);
  const [mceReadBackFailed, setMceReadBackFailed] = useState(false);

  // An event-class write stored a new rebuild: keep what the next precondition names.
  const onMceStoredChanged = useCallback(async () => {
    if (!adapterId) return;
    const apiBase = process.env.REACT_APP_LIFT_NET_CLOUD_API_ONLINE || "";
    const stored = await readStoredMapping(
      `${apiBase}/adapters/${adapterId}/cdo`,
      await GETJwtToken(),
    );
    // Without the server's bytes the form keeps no copy to name in a later precondition.
    setMceReadBackFailed(!stored);
    setCdo((prev) =>
      prev.cdoMce.stored
        ? {
            ...prev,
            cdoMce: { ...prev.cdoMce, stored: { kind: "embedded", binaryBase64: stored } },
          }
        : prev,
    );
  }, [adapterId, setCdo]);

  const onMceUploadBuilt = useCallback(
    (outcome: MceUploadOutcome | null) => {
      setMceUpload(outcome);
      setCdo((prev) => {
        const before = prev.cdoMce.stored?.replaces ?? prev.cdoMce;
        if (!outcome) {
          return prev.cdoMce.stored?.replaces ? { ...prev, cdoMce: before } : prev;
        }
        const built = decodeStoredMceMapping(outcome.cdoBinary);
        if (!built?.stored) return prev;
        return {
          ...prev,
          cdoMce: {
            ...built,
            stored: {
              ...built.stored,
              replaces: before,
            },
          },
        };
      });
    },
    [setCdo],
  );

  // ************************************************ */
  // LN1 HANDLERS ********************************** */
  const onChangeLn1Bool = (checked: boolean, variableName: string) => {
    setCdo((prev) => ({
      ...prev,
      cdoLn1: { ...prev.cdoLn1, [variableName]: checked },
    }));
  };

  const onChangeLn1Number = (value: number | null, variableName: string) => {
    setCdo((prev) => {
      const ln1 = { ...prev.cdoLn1 };
      if (variableName === "allowed_messages") {
        ln1.allowed_messages = value;
      } else {
        ln1.terminating_zeroes_expected = {
          ...ln1.terminating_zeroes_expected,
          [variableName]: value ?? 0,
        };
      }
      return { ...prev, cdoLn1: ln1 };
    });
  };

  // RVU-only — selects mapped/rvu/both heartbeat behavior.
  const onChangeLn1HeartbeatKind = (value: HeartbeatKind | null) => {
    setCdo((prev) => ({
      ...prev,
      cdoLn1: { ...prev.cdoLn1, heartbeat_kind: value },
    }));
  };

  // ************************************************ */
  // RENDER **************************************** */
  return (
    <div>
      <br />
      <DrawerRowDivider icon={faPlugCircleCheck} title={`${proto} Mapping Configuration`} />
      <div className={`drawer__box__${theme}`}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            marginBottom: 16,
          }}
        >
          <Button
            type="link"
            size="large"
            icon={<FontAwesomeIcon icon={faCodeBranch} />}
            onClick={() => navigate("/edge-management/schema-editor")}
          >
            Schema Editor
          </Button>
        </div>
        {/* ============================================= */}
        {/* MCE CDO */}
        {proto === "MCE" && (
          <div className="container">
            {cdo.cdoMce.stored && !cdo.cdoMce.stored.replaces ? (
              <Alert
                className="mt-3"
                type="info"
                showIcon
                message={mceNoteTitle(cdo.cdoMce, t)}
                description={mceNoteText(cdo.cdoMce, t)}
              />
            ) : null}
            {cdo.cdoMce.stored?.kind !== "legacy" && (
              <MceIdlUpload
                initialProduct={cdo.cdoMce.stored ? cdo.cdoMce.product_type : null}
                holdsBuild={Boolean(cdo.cdoMce.stored?.replaces)}
                onBuilt={onMceUploadBuilt}
              />
            )}
            {cdo.cdoMce.stored && (
              <>
                <div className="row">
                  <div className="col-12 col-md-6 mt-3">
                    <FormInputText
                      inputValue={cdo.cdoMce.idl_file}
                      inputLabelName={t("general.idlFile")}
                      inputOnChange={ignoreMceEdit}
                      inputVariableName="idl_file"
                      isRequired={false}
                      disabledInput
                      formularyType={FormularyType.WIZARD}
                    />
                  </div>
                  <div className="col-12 col-md-6 mt-3">
                    <span className="generalStyles__inputLabel">Product Type</span>
                    <Select
                      className="generalStyles__width100"
                      size="large"
                      value={cdo.cdoMce.product_type}
                      disabled
                      style={{ marginTop: 8 }}
                    >
                      {MCE_PRODUCT_TYPES.map((opt) => (
                        <Select.Option key={opt.value} value={opt.value}>
                          {opt.label}
                        </Select.Option>
                      ))}
                    </Select>
                  </div>
                </div>
                {mceEventEditable ? (
                  <MceLegacyEventFields
                    mappingFile={cdo.cdoMce.event.mapping_file}
                    mappingData={cdo.cdoMce.event.mapping_data}
                    onChange={onChangeMceEvent}
                    addressStatus={eventAddressStatus}
                  />
                ) : (
                  <div className="row">
                    <div className="col-12 col-md-4 mt-3">
                      <FormInputText
                        inputValue={cdo.cdoMce.event.mapping_file}
                        inputLabelName={t("general.eventMappingFile")}
                        inputOnChange={ignoreMceEdit}
                        inputVariableName="mapping_file"
                        isRequired={false}
                        disabledInput
                        formularyType={FormularyType.WIZARD}
                      />
                    </div>
                    <div className="col-12 col-md-4 mt-3">
                      <FormInputText
                        inputValue={cdo.cdoMce.event.mapping_data}
                        inputLabelName={t("general.eventMappingData")}
                        inputOnChange={ignoreMceEdit}
                        inputVariableName="mapping_data"
                        isRequired={false}
                        disabledInput
                        formularyType={FormularyType.WIZARD}
                      />
                    </div>
                  </div>
                )}
              </>
            )}
            {mceReadBackFailed && (
              <Alert className="mt-3" type="warning" showIcon message={READ_BACK_FAILED} />
            )}
            {cdo.cdoMce.stored?.kind === "embedded" && cdo.cdoMce.stored.binaryBase64 && (
              <MceMappingReview
                cdoBinaryBase64={cdo.cdoMce.stored.binaryBase64}
                summary={mceUpload?.result.summary ?? null}
                adapterId={cdo.cdoMce.stored.replaces ? undefined : adapterId}
                onStoredChanged={onMceStoredChanged}
                diagnostics={
                  mceUpload ? diagnosticRows(mceUpload.result.diagnostics, mceUpload.names) : []
                }
              />
            )}
          </div>
        )}

        {/* ============================================= */}
        {/* LN1 / RVU / CGA / LOBBYVISION CDO */}
        {["LN1", "RVU", "CGA", "LOBBYVISION"].includes(proto) && (
          <div className="container mt-3">
            <div className="row">
              <div className="col-12 col-md-4 mt-3">
                <span className="generalStyles__inputLabel">{t("general.crcOverride")}</span>
                <div style={{ marginTop: 8 }}>
                  <Switch
                    checked={cdo.cdoLn1.crc_override}
                    onChange={(checked) => onChangeLn1Bool(checked, "crc_override")}
                  />
                </div>
              </div>
              <div className="col-12 col-md-4 mt-3">
                <span className="generalStyles__inputLabel">
                  {t("general.shouldFilterDuplicates")}
                </span>
                <div style={{ marginTop: 8 }}>
                  <Switch
                    checked={cdo.cdoLn1.should_filter_duplicates}
                    onChange={(checked) => onChangeLn1Bool(checked, "should_filter_duplicates")}
                  />
                </div>
              </div>
              <div className="col-12 col-md-4 mt-3">
                <div className="generalStyles__inputFlexNameLarge">
                  <span className="generalStyles__inputFlexRequired">*</span>
                  <span className="drawer__text">{t("general.allowedMessages")}</span>
                </div>
                <div
                  className="generalStyles__width100"
                  style={{
                    marginTop: "9px",
                  }}
                >
                  <InputNumber
                    size="large"
                    min={0}
                    value={cdo.cdoLn1.allowed_messages}
                    placeholder="null"
                    onChange={(v) => onChangeLn1Number(v, "allowed_messages")}
                    style={{ width: "100%" }}
                  />
                </div>
              </div>
            </div>
            {proto === "RVU" && (
              <div className="row">
                <div className="col-12 col-md-4 mt-3">
                  <span className="generalStyles__inputLabel">{t("general.heartbeatKind")}</span>
                  <Select
                    className="generalStyles__width100"
                    size="large"
                    value={cdo.cdoLn1.heartbeat_kind}
                    onChange={(value: HeartbeatKind | null) => onChangeLn1HeartbeatKind(value)}
                    allowClear
                    style={{ marginTop: 8 }}
                  >
                    <Select.Option value={HeartbeatKind.Mapped}>Mapped toggle only</Select.Option>
                    <Select.Option value={HeartbeatKind.Rvu}>RVU keepalive only</Select.Option>
                    <Select.Option value={HeartbeatKind.Both}>Both</Select.Option>
                  </Select>
                </div>
              </div>
            )}
            <Divider orientation="left" plain>
              Terminating Zeroes Expected
            </Divider>
            <div className="row">
              {(
                [
                  ["from_device_serial", t("general.fromDeviceSerial")],
                  ["refresh_all", t("general.refreshAll")],
                  ["set_one_bit", t("general.setOneBit")],
                  ["reinitialize_all_devices", t("general.reinitializeAllDevices")],
                  ["set_one_byte", t("general.setOneByte")],
                  ["rvu", t("general.rvu")],
                  ["rvu_heartbeat", t("general.rvuHeartBeat")],
                ] as [string, string][]
              ).map(([key, label]) => (
                <div className="col-12 col-md-4 mt-3" key={key}>
                  <div className="generalStyles__inputFlexNameLarge">
                    <span className="generalStyles__inputFlexRequired">*</span>
                    <span className="drawer__text">{label}</span>
                  </div>
                  <div className="generalStyles__width100" style={{ marginTop: "9px" }}>
                    <InputNumber
                      size="large"
                      min={0}
                      value={
                        cdo.cdoLn1.terminating_zeroes_expected[
                          key as keyof typeof cdo.cdoLn1.terminating_zeroes_expected
                        ]
                      }
                      onChange={(v) => onChangeLn1Number(v, key)}
                      style={{ width: "100%" }}
                    />
                  </div>
                </div>
              ))}
            </div>
            <br />
            <CdoLn1MappingEditor
              directionAware={directionAware}
              mappings={cdo.ln1KeyMappings}
              onMappingsChange={(m) =>
                setCdo((prev) => {
                  // When a row that owned a composite-keyed individual offset entry
                  // is removed or has BOTH bank_level and individual flipped off,
                  // its composite key in ln1IndividualOffsets becomes stale — that
                  // phantom entry would persist in the matrix detection context and
                  // cause false "resolved" results for subsequent overlap checks.
                  // Prune any individual-offset entry whose composite key no longer
                  // matches an active own-column row (bank_level OR individual —
                  // matching extractLn1MappedTypes' ownColumnKeys partition).
                  const activeIndividualKeys = new Set(
                    m.filter((r) => r.bank_level || r.individual).map((r) => r.ln2MessageKey),
                  );
                  const cleanedIndividual = Object.fromEntries(
                    Object.entries(prev.ln1IndividualOffsets ?? {}).filter(([k]) =>
                      activeIndividualKeys.has(k),
                    ),
                  );
                  return {
                    ...prev,
                    ln1KeyMappings: m,
                    ln1IndividualOffsets: cleanedIndividual,
                  };
                })
              }
              unitOffsets={cdo.ln1UnitOffsets}
              onUnitOffsetsChange={(o) => setCdo((prev) => ({ ...prev, ln1UnitOffsets: o }))}
              intraContentFlags={cdo.ln1IntraContentFlags}
              onIntraContentFlagsChange={(f) =>
                setCdo((prev) => ({ ...prev, ln1IntraContentFlags: f }))
              }
              units={units}
              floors={floors}
              onUnitsChange={onUnitsChange || (() => {})}
              hideOffsetGrid
              individualOffsets={cdo.ln1IndividualOffsets}
            />
          </div>
        )}

        {/* ============================================= */}
        {/* MODBUS CDO — Visual Editor */}
        {proto === "MODBUS" && (
          <CdoModbusEditor
            cdo={cdo.cdoModbus}
            setCdo={(val: EditorModbusTemplate) => setCdo((prev) => ({ ...prev, cdoModbus: val }))}
          />
        )}

        {/* ============================================= */}
        {/* OPC CDO */}
        {proto === "OPC" && (
          <CdoOpcPage
            setCdo={(val: AdapterCdoOpcInterface) => setCdo((prev) => ({ ...prev, cdoOpc: val }))}
            cdoValue={cdo.cdoOpc}
          />
        )}

        {/* ============================================= */}
        {/* OPC UA CDO */}
        {proto === "OPCUA" && (
          <CdoOpcUaPage
            setCdo={(val: AdapterCdoOpcUa) => setCdo((prev) => ({ ...prev, cdoOpcUa: val }))}
            cdoValue={cdo.cdoOpcUa}
            // 1 is the adapter form's default when no floor count was saved, not a real count;
            // passing it would cap every range at floor 0.
            floors={floors > 1 ? floors : undefined}
          />
        )}

        {/* ============================================= */}
        {/* LOBBYVISION2 CDO */}
        {proto === "LOBBYVISION2" && (
          <CdoLobbyVision2Page
            setCdo={(val: AdapterCdoLobbyVision2) =>
              setCdo((prev) => ({ ...prev, cdoLobbyVision2: val }))
            }
            cdoValue={cdo.cdoLobbyVision2}
          />
        )}

        {/* ============================================= */}
        {/* SEQUENT CDO */}
        {proto === "SEQUENT" && (
          <CdoSequentPage
            setCdo={(val: AdapterCdoSequentInterface) =>
              setCdo((prev) => ({
                ...prev,
                cdoSequent: val,
              }))
            }
            cdoValue={cdo.cdoSequent}
          />
        )}

        {/* ============================================= */}
        {/* BACNET CDO */}
        {proto === "BACNET" && (
          <CdoBacnetPage
            setCdo={
              ((
                val:
                  | AdapterBacnetInterface[]
                  | ((prev: AdapterBacnetInterface[]) => AdapterBacnetInterface[]),
              ) => {
                setCdo((prev) => ({
                  ...prev,
                  cdoBacnet: typeof val === "function" ? val(prev.cdoBacnet) : val,
                }));
              }) as React.Dispatch<React.SetStateAction<AdapterBacnetInterface[]>>
            }
            cdoValue={cdo.cdoBacnet}
            bankMappings={cdo.bacnetBankMappings}
            setBankMappings={(val: any) =>
              setCdo((prev) => ({
                ...prev,
                bacnetBankMappings: typeof val === "function" ? val(prev.bacnetBankMappings) : val,
              }))
            }
            messageFilter={cdoMessageFilter.size > 0 ? cdoMessageFilter : undefined}
          />
        )}

        {/* ============================================= */}
        {/* LN2 / DILIGENT — no configuration needed */}
        {["LN2", "DILIGENT"].includes(proto) && (
          <div className="container generalStyles__textCenter">
            <p style={{ padding: 16, color: "#888" }}>
              {proto} protocol requires no additional mapping configuration.
            </p>
          </div>
        )}

        {/* ============================================= */}
        {/* STM32 CDO — Visual Editor */}
        {proto === "STM32" && (
          <CdoStm32Editor
            jsonValue={cdo.cdoJsonFallback}
            onChange={(json: string) =>
              setCdo((prev) => ({
                ...prev,
                cdoJsonFallback: json,
              }))
            }
            bankContext={bankContext}
          />
        )}

        {/* ============================================= */}
        {/* NUMATO RELAY CDO — Visual Editor */}
        {proto === NUMATO_RELAY && (
          <CdoNumatoRelayEditor
            jsonValue={cdo.cdoJsonFallback}
            onChange={(json: string) =>
              setCdo((prev) => ({
                ...prev,
                cdoJsonFallback: json,
              }))
            }
            bankContext={bankContext}
            defaultDevType={defaultDevType}
          />
        )}

        {/* ============================================= */}
        {/* NUMATO GPIO CDO — Visual Editor */}
        {proto === NUMATO_GPIO && (
          <CdoNumatoGpioEditor
            jsonValue={cdo.cdoJsonFallback}
            onChange={(json: string) =>
              setCdo((prev) => ({
                ...prev,
                cdoJsonFallback: json,
              }))
            }
            bankContext={bankContext}
            defaultDevType={defaultDevType}
          />
        )}
      </div>
      <br />
      {/* ============================================= */}
      {/* ONE-HOT EXCLUSION CONFIG — protocol-agnostic */}
      <DrawerRowDivider icon={faCodeBranch} title="One-Hot Exclusion Groups" />
      <div className={`drawer__box__${theme}`}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: 16,
          }}
        >
          <OneHotExclusionEditor
            config={cdo.oneHotExclusion ?? null}
            onChange={(newConfig) => {
              setCdo((prev) => ({ ...prev, oneHotExclusion: newConfig }));
            }}
            messageFilter={exclusionMessageFilter}
            readOnly={!isExclusionEditable(protocolType, cdo)}
          />
        </div>
      </div>
    </div>
  );
}
